
import { Component, Prop, Vue } from 'vue-property-decorator';
import { DataTableHeader } from 'vuetify';
import {
  DatabaseInterface,
  DatabaseRepositoryInterface,
  DatabaseUserInterface,
} from '@/services/Database/interfaces';
import { ServerInterface, ServerRepositoryInterface } from '@/services/Server/Interfaces';
import { Website } from '@/services/Website/interfaces';
import ViewDatabaseDialog from '@/components/dialogs/databases/ViewDatabaseDialog.vue';
import DeleteDatabaseDialog from '@/components/dialogs/databases/DeleteDatabaseDialog.vue';
import ViewDatabaseUserDialog from '@/components/dialogs/databases/ViewDatabaseUserDialog.vue';
import DeleteDatabaseUserDialog from '@/components/dialogs/databases/DeleteDatabaseUserDialog.vue';
import SyncDatabaseSizeDialog from '@/components/dialogs/databases/SyncDatabaseSizeDialog.vue';
import CreateDatabaseDialog from '@/components/dialogs/databases/CreateDatabaseDialog.vue';

@Component({
  components: {
    ViewDatabaseDialog,
    DeleteDatabaseDialog,
    ViewDatabaseUserDialog,
    DeleteDatabaseUserDialog,
    SyncDatabaseSizeDialog,
    CreateDatabaseDialog,
  },
  inject: [
    'databaseRepository',
    'serverRepository',
  ],
})
export default class WebsiteDatabasesPanel extends Vue {
  @Prop({ required: true }) private readonly website!: Website;

  private databaseRepository!: DatabaseRepositoryInterface;
  private serverRepository!: ServerRepositoryInterface;

  private loading: boolean = true;
  private databases: DatabaseInterface[] = [];
  private servers: ServerInterface[] = [];
  private expanded: DatabaseInterface[] = [];
  private statuses: string[] = ['CREATING', 'CREATED', 'ERROR', 'ACTIVE', 'DISABLED'];

  private createDatabaseDialog: string = '';
  private deleteDatabaseDialog: string = '';
  private deleteDatabaseForDialog: DatabaseInterface | false = false;
  private viewDatabaseDialog: string = '';
  private viewDatabaseForDialog: number | false = false;
  private viewUserDialog: string = '';
  private viewUserForDialog: number | false = false;
  private deleteUserDialog: string = '';
  private deleteUserForDialog: DatabaseUserInterface | false = false;
  private syncSizeDialog: string = '';
  private databaseForSyncDialog: DatabaseInterface = {} as DatabaseInterface;

  private headers: DataTableHeader[] = [
    { text: 'ID', value: 'id' },
    { text: 'Name', value: 'name' },
    { text: 'Server', value: 'server' },
    { text: 'Size (MB)', value: 'size' },
    { text: 'Status', value: 'status' },
    { text: 'Actions', value: 'actions', sortable: false },
    { text: '', value: 'data-table-expand' },
  ];

  private userHeaders: DataTableHeader[] = [
    { text: 'ID', value: 'id' },
    { text: 'Username', value: 'username' },
    { text: 'Status', value: 'status' },
    { text: 'Actions', value: 'actions', sortable: false },
  ];

  public async created(): Promise<void> {
    await this.loadDatabases();
  }

  private async loadDatabases(): Promise<void> {
    this.loading = true;
    try {
      this.databases = await this.databaseRepository.getByWebsiteId(this.website.id);
      const serverIds = this.databases.map((database) => database.server);
      if (serverIds.length) {
        this.servers = await this.serverRepository.getServersByIds(serverIds);
      }
    } catch (e) {
      this.$errorDialog.show(e.response?.data?.message || e.message);
    } finally {
      this.loading = false;
    }
  }

  private getServerName(serverId: string): string {
    const server = this.servers.find((s) => s._id === serverId);
    return server?.hostname || serverId;
  }

  private showCreateDatabaseDialog(): void {
    this.createDatabaseDialog = 'create-database-dialog';
  }

  private async onDatabaseCreated(): Promise<void> {
    this.createDatabaseDialog = '';
    await this.loadDatabases();
  }

  private viewDatabase(database: DatabaseInterface): void {
    this.viewDatabaseForDialog = database.id;
    this.viewDatabaseDialog = 'view-database-dialog';
  }

  private viewUser(user: DatabaseUserInterface): void {
    this.viewUserDialog = 'view-database-user-dialog';
    this.viewUserForDialog = user.id;
  }

  private async editUser(user: DatabaseUserInterface): Promise<void> {
    await this.$router.push({ name: 'database_user_edit', params: { userId: String(user.id) } });
  }

  private deleteUser(user: DatabaseUserInterface): void {
    this.deleteUserDialog = 'delete-database-user-dialog';
    this.deleteUserForDialog = user;
  }

  private deleteDatabase(database: DatabaseInterface): void {
    this.deleteDatabaseDialog = 'delete-database-dialog';
    this.deleteDatabaseForDialog = database;
  }

  private filterDatabases(database: DatabaseInterface): void {
    this.databases = this.databases.filter((item) => item.id !== database.id);
    this.viewDatabaseDialog = '';
    this.deleteDatabaseDialog = '';
  }

  private filterUsers(user: DatabaseUserInterface): void {
    this.databases.forEach((database) => {
      database.users = database.users.filter((u) => u.id !== user.id);
    });
  }

  private showSyncSizeDialog(database: DatabaseInterface): void {
    this.databaseForSyncDialog = database;
    this.syncSizeDialog = 'sync-database-size-dialog';
  }

  private async syncSize(database: DatabaseInterface): Promise<void> {
    try {
      await this.databaseRepository.syncDatabaseSize(database.id);
      this.$infoDialog.show(`Synchronization of database ${database.name} started.`);
    } catch (err) {
      this.$errorDialog.show(`Failed to start size synchronization of database ${database.name}`);
    }
  }
}
